import type { ReactNode } from 'react';
import { ArrowDown, ArrowRight, ArrowUp, Warning } from '@phosphor-icons/react';
import { cx } from '../cx';
import { Icon } from '../Icon';
import { TONE_TEXT, type Tone } from '../tone';
import type { ChartLayout } from './layout';
import { Sparkline } from './Sparkline';

// A card is narrower than a chart, so it switches at its own, smaller width (16rem: two cards side
// by side on a phone), not the charts' 28rem.
const FIGURE: Record<ChartLayout, string> = {
  auto: 'type-figure-sm @3xs:type-figure',
  narrow: 'type-figure-sm',
  wide: 'type-figure',
};
const WIDE_TREND: Record<ChartLayout, string> = { auto: '@max-3xs:hidden', narrow: 'hidden', wide: '' };

export type KpiDelta = {
  direction: 'up' | 'down' | 'flat';
  /** The change, formatted ("2%", "3 pts"). */
  display: ReactNode;
  /**
   * Coloured only when the direction is clearly good or bad for the reader (more ads that state pay:
   * positive). Ad counts stay neutral: more ads is not good or bad in itself.
   */
  tone?: Tone;
};

export type KpiCardProps = {
  label: ReactNode;
  /** The number, formatted. */
  value: ReactNode;
  delta?: KpiDelta;
  /** What the change is against, named ("vs last week", "vs Oct 2025"). */
  comparator?: ReactNode;
  /** Oldest first; 12 months is the usual run. */
  trend?: number[];
  /** One line that says what the number means ("About 1 in 4 ads"). */
  soWhat?: ReactNode;
  /** N and when it was updated ("5,017 of 19,391 ads. Updated 11 Oct, 06:00"). */
  basis: ReactNode;
  /** Set when the data is old ("Last data 3 days ago"): shown above the basis, in attention. */
  stale?: ReactNode;
  /** `auto` (default) switches to the narrow form in a narrow card: a smaller figure and no trend line. */
  layout?: ChartLayout;
  className?: string;
};

const ARROW = { up: ArrowUp, down: ArrowDown, flat: ArrowRight } as const;

/**
 * A summary number with its comparison, trend and basis, for the top of a city, role, company or
 * Data Desk page. Never for totals that only go up, and at most seven in a row. The comparator is
 * always named and stale data always labelled.
 */
export function KpiCard({
  label,
  value,
  delta,
  comparator,
  trend,
  soWhat,
  basis,
  stale,
  layout = 'auto',
  className,
}: KpiCardProps) {
  return (
    <div
      className={cx(
        '@container flex min-w-0 flex-col gap-1.5 rounded-jc-container bg-jc-surface p-4 ring-1 ring-jc-border ring-inset',
        className,
      )}
    >
      <span className="type-eyebrow text-jc-ink-secondary">{label}</span>
      <span className="flex flex-wrap items-baseline gap-x-2">
        <span className={cx('text-jc-ink', FIGURE[layout])}>{value}</span>
        {delta ? (
          <span
            className={cx('inline-flex items-center gap-1 type-meta font-semibold', TONE_TEXT[delta.tone ?? 'neutral'])}
          >
            <Icon icon={ARROW[delta.direction]} size="sm" weight="bold" />
            {delta.display}
            {comparator ? <span className="text-jc-ink-secondary">{comparator}</span> : null}
          </span>
        ) : null}
      </span>
      {trend && trend.length > 1 ? (
        // Wrapped, so hiding it never fights the sparkline's own display class.
        <span className={cx('flex', WIDE_TREND[layout])}>
          <Sparkline values={trend} className="w-full" />
        </span>
      ) : null}
      {soWhat ? <span className="type-meta text-jc-ink">{soWhat}</span> : null}
      {stale ? (
        <span className={cx('inline-flex items-center gap-1 type-caption font-semibold', TONE_TEXT.attention)}>
          <Icon icon={Warning} size="sm" weight="fill" />
          {stale}
        </span>
      ) : null}
      <span className="type-caption text-jc-ink-secondary">{basis}</span>
    </div>
  );
}
